import {
  Box,
  Checkbox,
  Divider,
  Fade,
  InputAdornment,
  makeStyles,
  Popover,
  PopoverOrigin,
  TextField,
  useTheme,
} from '@material-ui/core';

import ControlPointIcon from '@material-ui/icons/ControlPoint';
import LocalOfferOutlinedIcon from '@material-ui/icons/LocalOfferOutlined';
import SearchIcon from '@material-ui/icons/Search';
import { reachedEndOfList, THRESHOLD_50 } from '@src/utils/scrollUtils';
import { useEffect, useRef, useState } from 'react';
import TagLabel from './TagLabel';
import { AnyTag } from './types';
import { positions } from './utils';

const useStyles = makeStyles(theme => ({
  root: {
    color: theme.palette.primary.main,
  },
}));

export interface IAddTagWithScroll {
  label: string;
  title: string;
  placeholder?: string;
  tags: AnyTag[];
  handleUpdateTag: (tag: AnyTag, checked: boolean) => void | Promise<void>;
  position?: 'left' | 'under' | 'over';
  bgColor?: string;
  onSearchPatternChange: (value: string) => void;
  searchPattern: string;
  onNextPage: () => void;
  hasNextPage?: boolean;
}

const AddTagWithScroll: React.FC<IAddTagWithScroll> = ({
  tags,
  label,
  title,
  placeholder,
  handleUpdateTag,
  position = 'under',
  bgColor,
  onSearchPatternChange,
  searchPattern,
  onNextPage,
  hasNextPage,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.type === 'dark';
  const resolvedBgColor = bgColor ?? theme.palette.background.paper;
  const iconColor = isDark ? 'rgba(255,255,255,0.5)' : '#71717A';
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const [searchFilter, setSearchFilter] = useState('');

  const { root } = useStyles();

  const handleClose = () => {
    onSearchPatternChange('');
    setAnchorEl(null);
  };

  useEffect(() => {
    setSearchFilter(searchPattern);
  }, [searchPattern]);

  useEffect(() => {
    const timer = setTimeout(() => {
      onSearchPatternChange(searchFilter);
    }, 400);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchFilter]);

  return (
    <>
      <Box
        display="flex"
        alignItems="center"
        fontWeight={500}
        fontSize="14px"
        whiteSpace="nowrap"
        className={root}
        style={{ gap: '8px', cursor: 'pointer' }}
        onClick={(event: React.MouseEvent<HTMLElement>) =>
          setAnchorEl(anchorEl ? null : event.currentTarget)
        }
      >
        {label}
        <ControlPointIcon color="primary" fontSize="small" />
      </Box>

      <Popover
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        elevation={0}
        TransitionComponent={Fade}
        anchorOrigin={positions[position].anchorOrigin as PopoverOrigin}
        transformOrigin={positions[position].transformOrigin as PopoverOrigin}
      >
        <Box
          p="8px 7px"
          style={{
            borderRadius: '8px',
            background: resolvedBgColor,
            border: isDark ? '1px solid rgba(255,255,255,0.1)' : undefined,
            boxShadow: isDark
              ? '0 4px 20px rgba(0,0,0,0.4)'
              : '0 2px 8px rgba(0,0,0,0.15)',
          }}
          minWidth={241}
          maxWidth={302}
        >
          <Box mb={0.5} fontSize="0.9rem" fontWeight={600}>
            {title}
          </Box>
          <TextField
            placeholder={placeholder}
            variant="outlined"
            onChange={event => setSearchFilter(event.target.value)}
            value={searchFilter}
            style={{
              margin: '8px 0px',
              color: iconColor,
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LocalOfferOutlinedIcon
                    fontSize="small"
                    style={{ color: iconColor }}
                  />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon fontSize="small" style={{ color: iconColor }} />
                </InputAdornment>
              ),
              style: { height: '34px', paddingBlock: 0 },
            }}
          />
          <Divider style={{ marginInline: '-8px' }} />
          <div
            ref={listRef}
            style={{
              gap: '8px',
              minHeight: 50,
              maxHeight: 382,
              overflow: 'scroll',
              display: 'flex',
              flexDirection: 'column',
              paddingRight: 16,
              paddingTop: 4,
            }}
            onScroll={() => {
              if (reachedEndOfList(listRef, THRESHOLD_50) && hasNextPage) {
                onNextPage();
              }
            }}
          >
            {tags.map(tag => (
              <Box
                key={tag.name}
                display="flex"
                flexDirection="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <TagLabel key={tag.tagName} {...tag} size="small" ellipsis />
                <Checkbox
                  color="primary"
                  size="small"
                  style={{
                    padding: 0,
                  }}
                  checked={tag.assigned}
                  onChange={event => handleUpdateTag(tag, event.target.checked)}
                />
              </Box>
            ))}
          </div>
        </Box>
      </Popover>
    </>
  );
};

export default AddTagWithScroll;
